import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ApplianceGroup, DataRef } from '@shared/models/dataref.model';
import { combineLatest, combineLatestWith, distinctUntilChanged, Observable, Subject } from 'rxjs';
import { AutocompleteChoice } from '@shared/components/new-form-field-select-autocomplete/new-form-field-select-autocomplete.component';
import { map, startWith, tap } from 'rxjs/operators';

@Component({
  selector: 'dcp-appliance-groups-control',
  templateUrl: './appliance-groups-control.component.html',
  styleUrls: ['./appliance-groups-control.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ApplianceGroupsControlComponent implements OnInit {
  @Input()
  public allApplianceGroups$: Observable<ApplianceGroup[]>;

  @Input()
  public listOfApplianceGroupsCtrl: FormControl<string[]>;

  @Input()
  public appliancesAddButtonIdTag: string;
  @Input()
  public defaultApplianceGroupCtrl: FormControl<string | null>;

  @Input()
    autoSelectBtnIdTag: string;

  @Input()
  public defaultTranslation = 'DEFAULT';

  public allChoices$: Observable<AutocompleteChoice[]>;
  public selectedChoices$: Observable<AutocompleteChoice[]>;
  public selectAllGroups$: Subject<void> = new Subject();

  ngOnInit(): void {
    const defaultApplianceGroupChange$ = this.defaultApplianceGroupCtrl.valueChanges.pipe(
      startWith(this.defaultApplianceGroupCtrl.value),
      distinctUntilChanged(),
    );

    const allApplianceGroupsChange$ = this.allApplianceGroups$.pipe(distinctUntilChanged());

    const combineListAndDefaultParams = {
      defaultGroupKey: defaultApplianceGroupChange$,
      allApplianceGroups: allApplianceGroupsChange$,
    };

    this.allChoices$ = combineLatest(combineListAndDefaultParams).pipe(
      map(({ defaultGroupKey, allApplianceGroups }) => this.mapToAutocompleteChoices(allApplianceGroups, defaultGroupKey ?? undefined)),
    );

    this.selectedChoices$ = this.listOfApplianceGroupsCtrl.valueChanges.pipe(
      startWith(this.listOfApplianceGroupsCtrl.value),
      distinctUntilChanged(),
      tap((listOfApplianceGroupKeys) => {
        let currentDefault = this.defaultApplianceGroupCtrl.value;

        // check if default appliance group has been removed from chosen groups, if so reset control
        if (currentDefault && listOfApplianceGroupKeys.find((key) => key === currentDefault) === undefined) {
          currentDefault = null;
          this.setControlAndMarkAsDirty(this.defaultApplianceGroupCtrl, null);
        }

        // check if we must set a default appliance groups if none is set yet
        if (!currentDefault && listOfApplianceGroupKeys.length > 0) {
          this.setControlAndMarkAsDirty(this.defaultApplianceGroupCtrl, listOfApplianceGroupKeys[0]);
        }
      }),
      map((listOfApplianceGroupKeys) => new Set(listOfApplianceGroupKeys)),
      combineLatestWith(this.allChoices$),
      map(([setOfApplianceGroupKeys, allChoices]) => allChoices.filter((choice) => setOfApplianceGroupKeys.has(choice.key))),
    );
  }

  selectDefaultApplianceGroup(applianceGroupChoice: AutocompleteChoice): void {
    this.setControlAndMarkAsDirty(this.defaultApplianceGroupCtrl, applianceGroupChoice.key);
  }

  updateChosenApplianceGroupsInForm(applianceGroupChoices: AutocompleteChoice[]): void {
    const listOfApplianceGroupKeys = applianceGroupChoices.map((choice) => choice.key);
    this.setControlAndMarkAsDirty(this.listOfApplianceGroupsCtrl, listOfApplianceGroupKeys);
  }

  selectAllGroups(): void {
    this.selectAllGroups$.next();
  }

  private mapToAutocompleteChoices(applianceGroups: DataRef[], defaultApplianceGroupKey?: string): AutocompleteChoice[] {
    return applianceGroups.map((applianceGroup) => {
      const labelPrefix = defaultApplianceGroupKey === applianceGroup.key ? this.defaultTranslation + ' : ' : '';

      const applianceGroupChoice: AutocompleteChoice = {
        key: applianceGroup.key,
        label: labelPrefix + (applianceGroup.translatedName ?? applianceGroup.key),
      };

      return applianceGroupChoice;
    });
  }

  private setControlAndMarkAsDirty<T>(control: FormControl<T>, valueToSet: T): void {
    control.setValue(valueToSet);
    control.markAsDirty();
  }
}
